import React, { useState, useEffect } from 'react';
import { Button, DatePicker, useBreadcrumbRoutes, Wrapper, Form, message } from '@maxtropy/components';
import { Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import Cookies from 'js-cookie';
import { useRequest } from 'ahooks';
import {
  apiV2WaterFlowDataLeftPost,
  apiV2WaterFlowDataRightStatisticPost,
  apiV2WaterFlowDataRightChartHourPost,
  apiV2WaterFlowDataRightChartDayPost,
  apiV2WaterFlowConfigDetailPost,
} from '@maxtropy/device-customer-apis-v2';

import { Link, useSearchParams } from 'react-router-dom';
import { DateType, DateValue, DeviceRankItem, TimeRange } from './types';
import { getDefaultDateRange, validateDateRange, dayjsToTimestamp } from './utils';

import QueryTimeRangeSelector from './components/QueryTimeRangeSelector';
import LeftArea from './components/LeftArea';
import RightArea from './components/RightArea';

import styles from './index.module.scss';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';

const LongFlowMonitoring = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [searchParams] = useSearchParams();
  const hasConfig = useHasPermission(PermissionsType.B_LONGFLOWCONFIGURATION);

  // 状态管理
  const [dateRange, setDateRange] = useState<DateValue>(getDefaultDateRange());
  const [queryTimeRanges, setQueryTimeRanges] = useState<TimeRange[]>();
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [dateType, setDateType] = useState<DateType>(DateType.DAY);
  const [selectedDevice, setSelectedDevice] = useState<DeviceRankItem>();
  const [exporting, setExporting] = useState(false);

  // 从 URL 参数接收日期范围
  useEffect(() => {
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');
    if (startDate && endDate) {
      setDateRange([dayjs(startDate), dayjs(endDate)]);
    }
  }, [searchParams]);

  // 配置详情
  const { data: configData } = useRequest(() => apiV2WaterFlowConfigDetailPost({}), {
    onSuccess: res => {
      if (res.timePeriods) {
        setQueryTimeRanges(
          res.timePeriods.map(time => ({
            monitorStartTime: time.monitorStartTime!.split(':').slice(0, 2).join(':'),
            monitorEndTime: time.monitorEndTime!.split(':').slice(0, 2).join(':'),
          }))
        );
      }
    },
  });

  // 左侧数据请求
  const { data: leftData } = useRequest(
    () =>
      apiV2WaterFlowDataLeftPost({
        startTime: dateRange[0] ? dayjsToTimestamp(dateRange[0].startOf('day')) : undefined,
        endTime: dateRange[1] ? dayjsToTimestamp(dateRange[1].endOf('day')) : undefined,
        timePeriods: queryTimeRanges,
      }),

    {
      refreshDeps: [dateRange, queryTimeRanges],
      ready: !!queryTimeRanges,
      onSuccess: res => {
        const devices = res?.dataList || [];
        setSelectedDevice(devices.length > 0 ? { ...devices[0], rank: 1 } : undefined);
      },
    }
  );

  // 右侧统计数据请求
  const { data: rightStatisticData } = useRequest(
    () =>
      apiV2WaterFlowDataRightStatisticPost({
        startTime: dateRange[0] ? dayjsToTimestamp(dateRange[0].startOf('day')) : undefined,
        endTime: dateRange[1] ? dayjsToTimestamp(dateRange[1].endOf('day')) : undefined,
        timePeriods: queryTimeRanges,
        deviceId: selectedDevice?.deviceId,
      }),
    {
      refreshDeps: [dateRange, queryTimeRanges, selectedDevice?.deviceId],
      ready: !!selectedDevice?.deviceId && !!queryTimeRanges,
    }
  );

  // 右侧图表数据请求 - 小时维度
  const { data: chartHourData } = useRequest(
    () =>
      apiV2WaterFlowDataRightChartHourPost({
        startTime: dateRange[0] ? dayjsToTimestamp(dateRange[0].startOf('day')) : undefined,
        endTime: dateRange[1] ? dayjsToTimestamp(dateRange[1].endOf('day')) : undefined,
        timePeriods: queryTimeRanges,
        deviceId: selectedDevice?.deviceId,
        resolution: DateType.HOUR,
      }),
    {
      refreshDeps: [dateRange, queryTimeRanges, selectedDevice?.deviceId, dateType],
      ready: !!selectedDevice?.deviceId && dateType === DateType.HOUR && !!queryTimeRanges,
    }
  );

  // 右侧图表数据请求 - 天维度
  const { data: chartDayData } = useRequest(
    () =>
      apiV2WaterFlowDataRightChartDayPost({
        startTime: dateRange[0] ? dayjsToTimestamp(dateRange[0].startOf('day')) : undefined,
        endTime: dateRange[1] ? dayjsToTimestamp(dateRange[1].endOf('day')) : undefined,
        timePeriods: queryTimeRanges,
        deviceId: selectedDevice?.deviceId,
        resolution: DateType.DAY,
      }),
    {
      refreshDeps: [dateRange, queryTimeRanges, selectedDevice?.deviceId, dateType],
      ready: !!selectedDevice?.deviceId && dateType === DateType.DAY && !!queryTimeRanges,
    }
  );

  // 处理日期范围变化
  const handleDateRangeChange = (dates: DateValue | null) => {
    if (!validateDateRange(dates![0], dates![1])) {
      message.warning('时间跨度不能超过 31 天');
      return;
    }
    setDateRange(dates!);
  };

  // 处理添加查询时段
  const handleAddTimeRange = (range: { monitorStartTime: string; monitorEndTime: string }) => {
    setQueryTimeRanges([...(queryTimeRanges || []), range]);
    setShowTimePicker(false);
  };

  // 处理删除查询时段
  const handleRemoveTimeRange = (index: number) => {
    setQueryTimeRanges(queryTimeRanges?.filter((_, i) => i !== index));
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = Cookies.get('token');
      const { blob, disposition } = await new Promise<{ blob: Blob; disposition: string | null }>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/v2/waterFlow/data/export');
        xhr.responseType = 'blob';
        xhr.withCredentials = true;
        xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
        if (token) xhr.setRequestHeader('jwtToken', token);
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ blob: xhr.response, disposition: xhr.getResponseHeader('content-disposition') });
          } else {
            reject(new Error(`导出请求失败：${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('导出请求失败'));
        xhr.send(
          JSON.stringify({
            timestamp: Date.now(),
            startTime: dateRange[0] ? dayjsToTimestamp(dateRange[0].startOf('day')) : undefined,
            endTime: dateRange[1] ? dayjsToTimestamp(dateRange[1].endOf('day')) : undefined,
            timePeriods: queryTimeRanges,
          })
        );
      });

      const encodedFileName = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
      const normalFileName = disposition?.match(/filename="?([^";]+)"?/i)?.[1];
      const fileName = encodedFileName
        ? decodeURIComponent(encodedFileName)
        : normalFileName || `长流水监测${dayjs().format('YYYYMMDDHHmmss')}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch {
      message.error('导出失败，请稍后重试');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? [])]} hasPadding={false}>
      <Form isOrigin={true} style={{ display: 'flex', flexDirection: 'column', minHeight: 'inherit' }}>
        {/* 时间选择和查询时段 */}
        <div className={styles.topArea}>
          <Space size={20}>
            <Form.Item label="时间周期" style={{ marginBottom: 0 }}>
              <DatePicker.RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
                disabledDate={current => {
                  return current && current >= dayjs().startOf('day');
                }}
              />
            </Form.Item>
            <Form.Item label="查询时段" style={{ marginBottom: 0 }}>
              <QueryTimeRangeSelector
                queryTimeRanges={queryTimeRanges}
                showTimePicker={showTimePicker}
                onAddTimeRange={handleAddTimeRange}
                onRemoveTimeRange={handleRemoveTimeRange}
                onToggleTimePicker={setShowTimePicker}
              />
            </Form.Item>
          </Space>
          <Space size={8}>
            <Button type="primary" loading={exporting} disabled={!queryTimeRanges} onClick={handleExport}>
              导出
            </Button>
            {hasConfig && (
              <Link to="/longflow/monitoring/monitor/config">
                <Button type="primary">配置页</Button>
              </Link>
            )}
          </Space>
        </div>
        <div className={styles.mainArea}>
          {/* 左侧区域 */}
          <LeftArea
            dateRange={dateRange}
            leftData={leftData}
            pageLabel={configData?.description}
            selectedDevice={selectedDevice}
            onSelectDevice={setSelectedDevice}
          />

          {/* 右侧详情区域 */}
          <RightArea
            dateRange={dateRange}
            onChangeViewDimension={setDateType}
            selectedDevice={selectedDevice}
            rightStatisticData={rightStatisticData}
            dateType={dateType}
            pageLabel={configData?.description}
            chartDayData={chartDayData}
            chartHourData={chartHourData}
          />
        </div>
      </Form>
    </Wrapper>
  );
};

export default LongFlowMonitoring;
